/**
 * M21 #7 — 검수 상태 배지 + 상태 파생 헬퍼 (RecordTable / EditRecordModal 공용).
 */

import { AlertTriangle, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";
import type { RecordOut } from "@/lib/api/jobs";

import {
  deriveReviewState,
  REVIEW_STATE_I18N_KEYS,
  type ReviewDisplayState,
} from "./reviewStatusUtil";

const STYLES: Record<
  ReviewDisplayState,
  { cls: string; icon: typeof Circle; i18nKey: string; spin?: boolean }
> = {
  none: {
    cls: "border-slate-200 bg-slate-50 text-slate-400",
    icon: Circle,
    i18nKey: REVIEW_STATE_I18N_KEYS.none,
  },
  running: {
    cls: "border-violet-200 bg-violet-50 text-violet-700",
    icon: Loader2,
    i18nKey: REVIEW_STATE_I18N_KEYS.running,
    spin: true,
  },
  passed: {
    cls: "border-emerald-300 bg-emerald-50 text-emerald-700",
    icon: CheckCircle2,
    i18nKey: REVIEW_STATE_I18N_KEYS.passed,
  },
  needsReview: {
    cls: "border-red-300 bg-red-50 text-red-700",
    icon: AlertTriangle,
    i18nKey: REVIEW_STATE_I18N_KEYS.needsReview,
  },
  confirmed: {
    cls: "border-blue-600 bg-blue-600 text-white",
    icon: CheckCircle2,
    i18nKey: REVIEW_STATE_I18N_KEYS.confirmed,
  },
};

interface Props {
  record: RecordOut | null | undefined;
  className?: string;
}

/** 검수 상태를 작은 배지로 표시. ``none`` 은 옅은 muted 배지. */
export default function ReviewStatusBadge({ record, className }: Props) {
  const { t } = useTranslation();
  const state = deriveReviewState(record);
  const { cls, icon: Icon, i18nKey, spin } = STYLES[state];

  const issues = record?.ai_review?.issues ?? [];
  const title = state === "needsReview" && issues.length > 0 ? issues.join("\n") : undefined;

  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
        cls,
        className,
      )}
    >
      <Icon className={cn("h-3 w-3", spin && "animate-spin")} />
      {t(i18nKey)}
    </span>
  );
}
